import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth'
import { useState } from 'react'
import { FcGoogle } from 'react-icons/fc'
import { Link, useNavigate } from 'react-router-dom'
import { auth } from '../config/firebase'
import { AuthService } from '../services/authService'
import { Store, Zap, Shield, Bolt } from 'lucide-react'
import useToastStore from '../store/useToastStore'

export const LoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToastStore();

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);

      // Sign in with Google Firebase
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Get the ID token
      const idToken = await result.user.getIdToken();

      // Call backend API
      const response = await AuthService.googleLogin({ token: idToken });
      console.log(response);

      // Refresh Firebase token to get updated claims
      await AuthService.reFreshToken();

      // Redirect to home page (which will handle routing to dashboard if authenticated)
      if (response.isStoreSetup) {
        navigate('/admin');
      } else {
        navigate('/onboarding');
      }

    } catch (error) {
      console.error('Login error:', error);
      toast.showToast('Failed to sign in. Please try again.', 'error');
      // If there was an error, sign out from Firebase to maintain consistent state
      await AuthService.logout();
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Section - Login */}
      <div className="w-full md:w-[45%] min-h-screen bg-white flex items-center justify-center px-4 md:px-8 lg:px-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 mb-8">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
              <Store className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Mart-24
            </span>
          </Link>

          <div className="mb-8">
            <div className="inline-flex items-center px-3 py-1.5 mb-4 bg-purple-50 rounded-full border border-purple-200">
              <Zap className="w-3.5 h-3.5 text-purple-600 mr-1.5" />
              <span className="text-xs font-medium text-gray-700">Welcome Back!</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Sign in to your account
            </h2>
            <p className="text-gray-600">
              Access your dashboard and manage your store
            </p>
          </div>

          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className={`w-full flex items-center justify-center gap-3 bg-white text-gray-900 py-3 px-4 rounded-xl border border-gray-200 hover:border-purple-200 hover:shadow-lg transition-all duration-300 transform hover:scale-105 mb-6 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <FcGoogle className="text-xl" />
            )}
            <span className="font-medium">
              {isLoading ? 'Signing in...' : 'Continue with Google'}
            </span>
          </button>


        </div>
      </div>

      {/* Right Section - Hero Content */}
      <div className="hidden md:flex md:w-[55%] bg-gradient-to-br from-purple-50 via-white to-blue-50 items-center justify-center p-8 lg:p-12 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-1000"></div>
          <div className="absolute bottom-20 left-20 w-40 h-40 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-2000"></div>
        </div>

        <div className="max-w-2xl relative z-10">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-purple-100 p-8 shadow-xl">
            <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-4 animate-gradient">
              Create Your E-commerce Store in Minutes
            </h2>
            <p className="text-gray-600 text-lg mb-6">
              Launch your online store instantly with our powerful platform. Connect with your customers,
              manage products effortlessly, and grow your business with advanced analytics.
            </p>

            {/* Feature List */}
            <div className="space-y-4">
              {[
                { icon: Store, title: "Easy to use dashboard", color: "purple" },
                { icon: Shield, title: "Advanced analytics", color: "blue" },
                { icon: Bolt, title: "24/7 Customer support", color: "purple" }
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-${feature.color}-100 flex items-center justify-center`}>
                    <feature.icon className={`w-4 h-4 text-${feature.color}-600`} />
                  </div>
                  <p className="text-gray-700">{feature.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        .animation-delay-1000 { animation-delay: 1s; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  )
} 